/**
 * Modal para agregar residuos de limpiezas de playa a la guía
 * Adaptado para el generador de guías de warehouse
 * 
 * Permite seleccionar residuos recolectados en limpiezas de playa
 * para asociarlos a los detalles de la guía actual.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Row,
	Col,
	Label,
	Alert,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { ODataGrid } from '../../../../../Components/OElements/ODataGrid';
import { getResidueCollectedList } from '../../../../../slices/thunks';

// Selector FUERA del componente para evitar recreación en cada render
const selectResidueCollected = createSelector(
	state => state.ResidueCollecteds,
	data => ({
		dataList: data.residueCollectedList,
		isLoadingList: data.isLoadingList,
	})
);

// Columnas FUERA del componente - son estáticas
const COLUMNS = [
	{
		header: 'Residuo',
		accessorKey: 'residue.name',
		size: 'auto',
		Cell: ({ row }) => (
			<div className='no-nowrap'>
				{row.original.residue?.name || '-'}
			</div>
		),
	},
	{
		header: 'Kilogramos',
		accessorKey: 'kilograms',
		size: 'auto',
	},
	{
		header: 'Fecha Limpieza',
		accessorKey: 'beach_cleaning.date',
		size: 'auto',
		dataType: 'date',
	},
	{
		header: 'Sector',
		accessorKey: 'beach_cleaning.sector.name',
		size: 'auto',
	},
];

export const BeachCleaningModal = ({
	isOpen,
	toggle,
	onSelectCleanings,
	currentResidueIds = [],
}) => {
	const dispatch = useDispatch();
	const [selectionList, setSelectionList] = useState({});
	const hasLoadedRef = useRef(false);

	const { dataList, isLoadingList } = useSelector(selectResidueCollected);

	// Cargar residuos recolectados disponibles - solo cuando se abre el modal
	useEffect(() => {
		if (isOpen && !hasLoadedRef.current) {
			const params = {
				residue_collected_status_code: 'AVA', // Solo disponibles
			};

			dispatch(getResidueCollectedList(params));
			setSelectionList({}); // Limpiar selección al abrir
			hasLoadedRef.current = true;
		}
		
		// Reset cuando se cierra el modal
		if (!isOpen) {
			hasLoadedRef.current = false;
		}
	}, [isOpen, dispatch]);

	// Confirmar selección
	const handleConfirm = () => {
		const selectedIds = Object.keys(selectionList).filter(
			key => selectionList[key]
		);
		const selectedCleanings = selectedIds
			.map(id => dataList.find(item => item._id === id))
			.filter(Boolean);

		if (selectedCleanings.length === 0) {
			return; // No hacer nada si no hay selección
		}

		onSelectCleanings(selectedCleanings);
		toggle();
	};

	const handleClose = () => {
		setSelectionList({});
		toggle();
	};

	return (
		<Modal isOpen={isOpen} toggle={handleClose} size='xl'>
			<ModalHeader toggle={handleClose}>
				Agregar Residuos De Limpieza
			</ModalHeader>
			<ModalBody>
				<Row className='g-3'>
					<Col lg={12}>
						<Alert color='info' className='mb-3'>
							<i className='ri-information-line me-1'></i>
							Seleccione los residuos recolectados en limpiezas de playa
							que desea asociar a esta guía. Si selecciona limpiezas de un
							residuo que aún no está en la guía, se creará automáticamente
							un nuevo item para ese residuo.
						</Alert>
					</Col>
					<Col lg={12}>
						<Label className='form-label'>
							Lista de residuos recolectados disponibles
						</Label>
						<ODataGrid
							columns={COLUMNS}
							data={dataList || []}
							isLoadingData={isLoadingList}
							getRowIdTable={originalRow => originalRow._id}
							selectAllMode={'all'}
							selection={{
								rowSelection: selectionList,
								setRowSelection: setSelectionList,
							}}
						/>
					</Col>
				</Row>
			</ModalBody>
			<ModalFooter>
				<div className='hstack gap-2'>
					<Button type='button' onClick={handleClose} className='btn-danger'>
						<i className='ri-close-line me-1'></i>
						Cancelar
					</Button>
					<Button
						type='button'
						color='primary'
						onClick={handleConfirm}
						disabled={Object.keys(selectionList).length === 0}
					>
						<i className='ri-save-line me-1'></i>
						Agregar Residuos Seleccionados (
						{Object.keys(selectionList).filter(k => selectionList[k]).length})
					</Button>
				</div>
			</ModalFooter>
		</Modal>
	);
};
